import { apiClient } from '@/utils/api-client';
import type { ApiResponse, JsonApiResource } from '@/types/api';

export interface BlockingElement {
  scope_key: string;
  slot: string;
  title: string | null;
  reason?: string;
}

export interface GuidedCanvasSummary {
  canvas_key: string;
  title: string | null;
  description: string | null;
  locale: string;
  published: boolean;
  position: number | null;
  element_count: number;
  authored_count: number;
  coverage_label: string;
  publishable: boolean;
  blocking_elements: BlockingElement[];
}

export interface GuidedElementRow {
  scope_key: string;
  canvas_key: string;
  slot: string;
  title: string | null;
  sub: string | null;
  source: 'header' | 'milestone' | 'derived';
  reference_completion_criteria: string | null;
  guidance_id: number | null;
  authored: boolean;
  updated_at: string | null;
  updated_by: string | null;
  definition_preview: string | null;
}

export interface GuidedCanvasDetail extends GuidedCanvasSummary {
  elements: GuidedElementRow[];
}

export interface RelatedResourceOption {
  id: string;
  title: string;
  type?: string | null;
  program_code?: string | null;
  has_ai_content: boolean;
}

export interface LinkedResource {
  contentful_entry_id: string;
  title: string;
  position?: number;
}

export interface ElementStandard {
  id: string;
  scope_key: string;
  locale: string;
  definition_of_good: string | null;
  dos: string[];
  donts: string[];
  worked_example: string | null;
  completion_criteria: string | null;
  socratic_prompts: string[];
  resources: LinkedResource[];
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  canvas_key: string;
  slot: string;
  title: string | null;
  reference_completion_criteria: string | null;
}

export interface PromptPreview {
  scope_key: string;
  locale: string;
  authored: boolean;
  budget: number;
  definition_of_good?: string | null;
  dos?: string[];
  donts?: string[];
  worked_example?: string | null;
  completion_criteria?: string | null;
  block?: string | null;
  character_count?: number;
  resources?: { id: string; title: string; preview: string }[];
  resource_character_count?: number;
  resource_ceiling?: number;
  resources_within_ceiling?: boolean;
  within_budget?: boolean;
  omitted_sections?: string[];
}

export interface ElementPayload {
  element: GuidedElementRow;
  guidance: ApiResponse<JsonApiResource<ElementStandard>> | null;
  preview: PromptPreview;
}

export interface StandardWriteData {
  scope_key?: string;
  locale?: string;
  definition_of_good?: string | null;
  dos?: string[];
  donts?: string[];
  worked_example?: string | null;
  completion_criteria?: string | null;
  socratic_prompts?: string[];
  resources?: LinkedResource[];
}

export type ImportAction = 'create' | 'update' | 'unchanged' | 'empty' | 'error';

export interface ImportRowChange {
  from: unknown;
  to: unknown;
}

export interface ImportRow {
  row_number: number;
  canvas_key: string;
  slot: string;
  scope_key: string;
  locale: string;
  action: ImportAction;
  errors: string[];
  changes: Record<string, ImportRowChange>;
  attributes: Record<string, unknown>;
  existing_updated_at?: string | null;
  source_name?: string | null;
}

export interface ImportSummary {
  total: number;
  create: number;
  update: number;
  unchanged: number;
  empty: number;
  error: number;
}

export interface ImportSource {
  name: string;
  content: string;
}

export interface ImportPreview {
  locale: string;
  delimiter: string;
  columns: string[];
  unknown_columns: string[];
  rows: ImportRow[];
  summary: ImportSummary;
}

export interface ImportResult extends ImportPreview {
  written: number;
  skipped: number;
  aborted: boolean;
  error?: string;
}

const STANDARDS_PATH = '/api/admin/element_standards';
const CANVASES_PATH = '/api/admin/guided_use_cases';

export const SCOPE_KEY_SEPARATOR = '#';

/**
 * @param {string} canvasKey - the canvas the element belongs to
 * @param {string} slot - the element's slot id within that canvas
 * @returns {string} the scope key the API anchors a standard on
 */
export function scopeKeyFor(canvasKey: string, slot: string): string {
  return `${canvasKey}${SCOPE_KEY_SEPARATOR}${slot}`;
}

export const guidedUseCaseService = {
  async listCanvases(): Promise<{ data: GuidedCanvasSummary[]; locale: string }> {
    return apiClient.get(CANVASES_PATH);
  },

  async getCanvas(canvasKey: string): Promise<{ data: GuidedCanvasDetail }> {
    return apiClient.get(`${CANVASES_PATH}/${canvasKey}`);
  },


  async unpublishCanvas(canvasKey: string): Promise<{ data: GuidedCanvasDetail }> {
    return apiClient.post(`${CANVASES_PATH}/${canvasKey}/unpublish`);
  },

  async reorderCanvases(
    canvasKeys: string[]
  ): Promise<{ data: GuidedCanvasSummary[]; locale: string }> {
    return apiClient.post(`${CANVASES_PATH}/reorder`, { canvas_keys: canvasKeys });
  },

  async publishCanvas(
    canvasKey: string
  ): Promise<{ data: GuidedCanvasDetail }> {
    return apiClient.post(`${CANVASES_PATH}/${canvasKey}/publish`);
  },

  async getElement(scopeKey: string): Promise<ElementPayload> {
    return apiClient.get(`${STANDARDS_PATH}/element`, { scope_key: scopeKey });
  },

  async createStandard(
    data: StandardWriteData
  ): Promise<ApiResponse<JsonApiResource<ElementStandard>>> {
    return apiClient.post(STANDARDS_PATH, { guidance: data });
  },

  async updateStandard(
    id: string,
    data: StandardWriteData
  ): Promise<ApiResponse<JsonApiResource<ElementStandard>>> {
    return apiClient.patch(`${STANDARDS_PATH}/${id}`, { guidance: data });
  },


  async searchRelatedResources(query: string): Promise<{ data: RelatedResourceOption[] }> {
    return apiClient.get(`${STANDARDS_PATH}/related_resources`, { q: query });
  },

  async previewPrompt(
    scopeKey: string,
    guidance?: StandardWriteData
  ): Promise<{ data: PromptPreview }> {
    return apiClient.post(`${STANDARDS_PATH}/preview`, { scope_key: scopeKey, guidance });
  },

  async importPreview(sources: ImportSource[], canvasKey?: string): Promise<ImportPreview> {
    return apiClient.post(`${STANDARDS_PATH}/import_preview`, { sources, canvas_key: canvasKey });
  },

  async importApply(sources: ImportSource[], canvasKey?: string): Promise<ImportResult> {
    return apiClient.post(`${STANDARDS_PATH}/import`, { sources, canvas_key: canvasKey });
  },

  /**
   * @param {string|undefined} canvasKey - a single canvas, or undefined for every canvas
   * @returns {Promise<{ blob: Blob; filename: string }>} the template sheet and the name to save it under
   */
  async downloadTemplate(canvasKey?: string): Promise<{ blob: Blob; filename: string }> {
    const base = `${import.meta.env.VITE_API_BASE_URL}${STANDARDS_PATH}/template`;
    const url = canvasKey ? `${base}?canvas_key=${encodeURIComponent(canvasKey)}` : base;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) throw new Error('Could not build the template');

    const disposition = response.headers.get('content-disposition') ?? '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    return { blob: await response.blob(), filename: match?.[1] ?? 'kaiya-element-standards.csv' };
  },
};
